import { useQueryClient } from '@tanstack/react-query'
import { createContext, useCallback, useContext, useMemo } from 'react'
import { queryKeys } from '../queries/queryKeys'
import { useRichText } from '../queries/useRichText'

type DocumentContextValue = {
  id: string
  invalidateDocumentQuery: () => void
}

type DocumentProviderProps = {
  id: string
  children: React.ReactNode
}

const DocumentContext = createContext<DocumentContextValue | undefined>(
  undefined
)

export const DocumentProvider: React.FC<DocumentProviderProps> = ({
  children,
  id,
}) => {
  const queryClient = useQueryClient()

  // Preloads rich text fetch for extensions that need to receive data
  useRichText({ documentId: id })

  const invalidateDocumentQuery = useCallback(() => {
    queryClient.invalidateQueries(queryKeys.document(id))
  }, [id, queryClient])

  const value = useMemo(
    () => ({ id, invalidateDocumentQuery }),
    [id, invalidateDocumentQuery]
  )

  return (
    <DocumentContext.Provider value={value}>
      {children}
    </DocumentContext.Provider>
  )
}

export const useDocument = () => {
  return useContext(DocumentContext)
}
